/**
 * Permission utilities for role-based access control
 */

/**
 * Role constants
 */
export const ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin',
  EDITOR: 'editor',
  BRIEF_EDITOR: 'brief_editor',
  VIEWER: 'viewer',
};

/**
 * Role levels for permission comparison
 * Higher number = more permissions
 */
export const ROLE_LEVELS = {
  [ROLES.OWNER]: 5,
  [ROLES.ADMIN]: 4,
  [ROLES.EDITOR]: 3,
  [ROLES.BRIEF_EDITOR]: 2,
  [ROLES.VIEWER]: 1,
};

/**
 * Get the numeric level for a role
 * @param {string} role - Role name
 * @returns {number} Role level (0 if invalid)
 */
export const getRoleLevel = (role) => {
  return ROLE_LEVELS[role] || 0;
};

/**
 * Check if user can view content (level >= 1)
 * @param {string} role - User's role
 * @returns {boolean}
 */
export const canView = (role) => {
  return getRoleLevel(role) >= ROLE_LEVELS[ROLES.VIEWER];
};

/**
 * Check if user can edit briefs (level >= 2)
 * Edit campaign briefs and configuration
 * @param {string} role - User's role
 * @returns {boolean}
 */
export const canEditBrief = (role) => {
  return getRoleLevel(role) >= ROLE_LEVELS[ROLES.BRIEF_EDITOR];
};

/**
 * Check if user can edit content (level >= 3)
 * Create/edit projects, advertisers
 * @param {string} role - User's role
 * @returns {boolean}
 */
export const canEdit = (role) => {
  return getRoleLevel(role) >= ROLE_LEVELS[ROLES.EDITOR];
};

/**
 * Check if user can manage users (level >= 4)
 * Invite/remove users, change roles, edit org settings
 * @param {string} role - User's role
 * @returns {boolean}
 */
export const canManageUsers = (role) => {
  return getRoleLevel(role) >= ROLE_LEVELS[ROLES.ADMIN];
};

/**
 * Check if user can manage billing (level >= 5)
 * View/purchase credits, delete org, transfer ownership
 * @param {string} role - User's role
 * @returns {boolean}
 */
export const canManageBilling = (role) => {
  return getRoleLevel(role) >= ROLE_LEVELS[ROLES.OWNER];
};

/**
 * Check if user has at least a specific role level
 * @param {string} role - User's role
 * @param {number} requiredLevel - Required level
 * @returns {boolean}
 */
export const hasMinLevel = (role, requiredLevel) => {
  return getRoleLevel(role) >= requiredLevel;
};

/**
 * Get user's role in a specific organization
 * @param {Array} organizations - User's organizations array
 * @param {string} orgId - Organization ID to check
 * @returns {string|null} Role or null if not a member
 */
export const getUserRoleInOrg = (organizations, orgId) => {
  if (!organizations || !orgId) return null;
  const org = organizations.find(o => o.id === orgId);
  return org?.role || null;
};

/**
 * Get user's role in a specific organization by slug
 * @param {Array} organizations - User's organizations array
 * @param {string} orgSlug - Organization slug to check
 * @returns {string|null} Role or null if not a member
 */
export const getUserRoleInOrgBySlug = (organizations, orgSlug) => {
  if (!organizations || !orgSlug) return null;
  const org = organizations.find(o => o.slug === orgSlug);
  return org?.role || null;
};

/**
 * Get all permissions for a role as an object
 * @param {string} role - User's role
 * @returns {Object} Permission flags
 */
export const getPermissions = (role) => {
  return {
    canView: canView(role),
    canEditBrief: canEditBrief(role),
    canEdit: canEdit(role),
    canManageUsers: canManageUsers(role),
    canManageBilling: canManageBilling(role),
    level: getRoleLevel(role),
    role,
  };
};

/**
 * Human-readable role labels
 */
export const ROLE_LABELS = {
  [ROLES.OWNER]: 'Owner',
  [ROLES.ADMIN]: 'Admin',
  [ROLES.EDITOR]: 'Editor',
  [ROLES.BRIEF_EDITOR]: 'Brief Editor',
  [ROLES.VIEWER]: 'Viewer',
};

/**
 * Role descriptions for UI
 */
export const ROLE_DESCRIPTIONS = {
  [ROLES.OWNER]: 'Full control including billing and organization deletion',
  [ROLES.ADMIN]: 'Manage users, settings, and all content',
  [ROLES.EDITOR]: 'Create and edit projects and advertisers',
  [ROLES.BRIEF_EDITOR]: 'Edit campaign briefs and video configuration',
  [ROLES.VIEWER]: 'View-only access to content',
};
